import React, { useState, useEffect, useRef } from 'react';
import { Settings, Upload, Check, Image, Loader2, Lock, Unlock, Sparkles, Edit2, Trash2, X, Sun, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map animation types to CSS classes
const ANIMATION_CLASSES = {
  'breathe': 'animate-breathe',
  'zoom-slow': 'animate-zoom-slow',
  'pan-right': 'animate-pan-right',
  'pan-left': 'animate-pan-left',
  'pulse-glow': 'animate-pulse-glow',
  'float': 'animate-float'
};

export const BackgroundLoop = ({ onBackgroundChange }) => {
  const [backgrounds, setBackgrounds] = useState([]);
  const [currentBg, setCurrentBg] = useState(null);
  const [showSelector, setShowSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [editingBg, setEditingBg] = useState(null);
  const [editBgName, setEditBgName] = useState('');
  const [bgOpacity, setBgOpacity] = useState(() => {
    const saved = localStorage.getItem('home_bg_opacity');
    return saved ? parseInt(saved) : 15;
  });
  const [bgBrightness, setBgBrightness] = useState(() => {
    const saved = localStorage.getItem('home_bg_brightness');
    return saved ? parseInt(saved) : 85;
  });
  const fileInputRef = useRef(null);

  const handleOpacityChange = (v) => { setBgOpacity(v); localStorage.setItem('home_bg_opacity', v); };
  const handleBrightnessChange = (v) => { setBgBrightness(v); localStorage.setItem('home_bg_brightness', v); };

  useEffect(() => {
    fetchBackgrounds();
  }, []);

  const fetchBackgrounds = async () => {
    try {
      const response = await axios.get(`${API}/backgrounds`);
      setBackgrounds(response.data);
      if (response.data.length > 0 && !currentBg) {
        setCurrentBg(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching backgrounds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBg = (bg) => {
    setCurrentBg(bg);
    if (onBackgroundChange) {
      onBackgroundChange(bg);
    }
    toast.success(`Fond "${bg.name}" appliqué`);
  };

  const handleRenameBg = async (bgId) => {
    if (!editBgName.trim()) return;
    try {
      const formData = new FormData();
      formData.append('name', editBgName);
      await axios.put(`${API}/backgrounds/${bgId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Fond renommé');
      setEditingBg(null);
      fetchBackgrounds();
    } catch (e) {
      toast.error('Erreur lors du renommage');
    }
  };

  const handleToggleLockBg = async (bg) => {
    try {
      const formData = new FormData();
      formData.append('locked', (!bg.locked).toString());
      await axios.put(`${API}/backgrounds/${bg.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(bg.locked ? 'Fond déverrouillé' : 'Fond verrouillé');
      fetchBackgrounds();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteBg = async (bg) => {
    if (bg.locked) {
      toast.error('Déverrouillez ce fond avant de le supprimer');
      return;
    }
    if (!window.confirm(`Supprimer le fond "${bg.name}" ?`)) return;
    try {
      await axios.delete(`${API}/backgrounds/${bg.id}`);
      toast.success('Fond supprimé');
      if (currentBg?.id === bg.id) setCurrentBg(null);
      fetchBackgrounds();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!uploadName.trim()) {
      toast.error('Veuillez entrer un nom pour le fond');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', uploadName);

    try {
      const response = await axios.post(`${API}/backgrounds/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Fond personnalisé ajouté');
      setUploadName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchBackgrounds();
      // Auto-select the new background
      setCurrentBg(response.data);
    } catch (error) {
      console.error('Error uploading background:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (bg) => {
    if (!bg) return '';
    if (bg.url.startsWith('http')) return bg.url;
    return `${process.env.REACT_APP_BACKEND_URL}${bg.url}`;
  };

  // Get animation class based on background animation type
  const getAnimationClass = (bg) => {
    if (!bg || !bg.animation) return 'animate-breathe';
    return ANIMATION_CLASSES[bg.animation] || 'animate-breathe';
  };

  return (
    <>
      {/* Background Image */}
      <div className="zen-background">
        {currentBg && (
          <img
            key={currentBg.id}
            src={getImageUrl(currentBg)}
            alt={currentBg.name}
            className={getAnimationClass(currentBg)}
            style={{ filter: `brightness(${bgBrightness / 100})` }}
          />
        )}
        {/* Overlay with adjustable opacity */}
        <div className="absolute inset-0 bg-black transition-opacity" style={{ opacity: bgOpacity / 100, zIndex: 1 }} />
      </div>

      {/* Background Selector Toggle */}
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full glass flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
        data-testid="bg-selector-toggle"
      >
        <Image className="w-5 h-5" />
      </button>

      {/* Background Selector Panel */}
      {showSelector && (
        <div className="fixed bottom-36 right-4 z-40 w-80 max-h-[60vh] overflow-y-auto glass rounded-2xl p-4" data-testid="bg-selector-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-white font-semibold">Fond d'écran</h3>
            <button
              onClick={() => setShowSelector(false)}
              className="text-white/60 hover:text-white"
            >
              ×
            </button>
          </div>

          {/* Visual Settings */}
          <div className="mb-4 p-3 bg-white/5 rounded-xl border border-white/10 space-y-3">
            <p className="text-sm text-white/60 font-medium">Réglages visuels</p>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-white/50 text-xs flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Transparence</span>
                <span className="text-white/70 text-xs">{bgOpacity}%</span>
              </div>
              <Slider value={[bgOpacity]} onValueChange={([v]) => handleOpacityChange(v)} max={90} min={0} step={5} className="w-full" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-white/50 text-xs flex items-center gap-1.5"><Sun className="w-3.5 h-3.5" /> Éclairage</span>
                <span className="text-white/70 text-xs">{bgBrightness}%</span>
              </div>
              <Slider value={[bgBrightness]} onValueChange={([v]) => handleBrightnessChange(v)} max={100} min={10} step={5} className="w-full" />
            </div>
          </div>

          {/* Upload Custom Background */}
          <div className="mb-4 p-3 bg-white/5 rounded-xl border border-white/10">
            <p className="text-sm text-white/60 mb-2">Votre propre fond</p>
            <Input
              placeholder="Nom du fond"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              className="mb-2 bg-white/5 border-white/10 text-white text-sm placeholder:text-white/40"
              data-testid="bg-upload-name"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="bg-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !uploadName.trim()}
              size="sm"
              className="w-full bg-white/10 hover:bg-white/20 text-white"
              data-testid="bg-upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {uploading ? 'Upload...' : 'Choisir une image'}
            </Button>
          </div>

          {/* Background Grid */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {backgrounds.map((bg) => {
                const isSelected = currentBg?.id === bg.id;
                const isEditing = editingBg === bg.id;
                return (
                  <div key={bg.id} className="relative group">
                    <button
                      onClick={() => handleSelectBg(bg)}
                      className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all w-full ${
                        isSelected
                          ? 'border-amber-400 ring-2 ring-amber-400/30'
                          : 'border-transparent hover:border-white/30'
                      }`}
                      data-testid={`bg-option-${bg.id}`}
                    >
                      <img
                        src={getImageUrl(bg)}
                        alt={bg.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                          <Check className="w-3 h-3 text-slate-900" />
                        </div>
                      )}
                      {bg.animation && bg.animation !== 'breathe' && (
                        <div className="absolute top-1 left-7 px-1.5 py-0.5 rounded bg-purple-500/80 flex items-center gap-1" title={`Animation: ${bg.animation}`}>
                          <Sparkles className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                        <p className="text-white text-xs truncate">{bg.name}</p>
                      </div>
                    </button>

                    {/* Admin controls on hover */}
                    <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5 z-10">
                      <button onClick={(e) => { e.stopPropagation(); setEditingBg(bg.id); setEditBgName(bg.name); }} className="p-1 bg-black/70 rounded text-white/80 hover:text-white" title="Renommer">
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleToggleLockBg(bg); }} className={`p-1 bg-black/70 rounded ${bg.locked ? 'text-amber-400' : 'text-white/80 hover:text-amber-400'}`} title={bg.locked ? 'Déverrouiller' : 'Verrouiller'}>
                        {bg.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                      </button>
                      {!bg.locked && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteBg(bg); }} className="p-1 bg-black/70 rounded text-white/80 hover:text-red-400" title="Supprimer">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Inline rename */}
                    {isEditing && (
                      <div className="absolute inset-0 bg-black/80 rounded-lg flex items-center justify-center p-2 z-20" onClick={(e) => e.stopPropagation()}>
                        <input
                          value={editBgName}
                          onChange={(e) => setEditBgName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameBg(bg.id); if (e.key === 'Escape') setEditingBg(null); }}
                          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs w-full mr-1"
                          autoFocus
                        />
                        <button onClick={() => handleRenameBg(bg.id)} className="p-1 bg-amber-500 rounded text-white"><Check className="w-3 h-3" /></button>
                        <button onClick={() => setEditingBg(null)} className="p-1 bg-white/10 rounded text-white ml-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BackgroundLoop;
